import type { InboundDockShipment } from "./inboundApi";

export type InboundArrivalsTimePeriod = {
  from: string;
  to: string;
};

export function chicagoDateToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  return `${year}-${month}-${day}`;
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addCalendarDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatIsoDate(date);
}

/** Default dock-arrivals window: last two weeks through today (America/Chicago). */
export function defaultArrivalsTimePeriod(days = 14): InboundArrivalsTimePeriod {
  const to = chicagoDateToday();
  return { from: addCalendarDays(to, -days), to };
}

/** Mirrors server latestDockShipmentActivity: latest crate log, else expected arrival. */
export function latestInboundDockShipmentActivityDate(
  shipment: Pick<InboundDockShipment, "expected_arrival" | "units">,
): string {
  const latestUnit = shipment.units
    .map((unit) => unit.logged_at)
    .filter((loggedAt) => loggedAt.trim() !== "")
    .sort()
    .at(-1);
  const raw = latestUnit ?? shipment.expected_arrival ?? "";
  if (raw.trim() === "") return "";
  return raw.includes("T") ? raw.slice(0, 10) : raw.trim().slice(0, 10);
}

export function inboundDockShipmentInPeriod(
  shipment: InboundDockShipment,
  from: string,
  to: string,
): boolean {
  const activityDate = latestInboundDockShipmentActivityDate(shipment);
  if (activityDate === "") return false;
  return activityDate >= from && activityDate <= to;
}

export function filterInboundDockShipmentsByPeriod(
  shipments: InboundDockShipment[],
  from: string,
  to: string,
): InboundDockShipment[] {
  if (from > to) return [];
  return shipments.filter((shipment) => inboundDockShipmentInPeriod(shipment, from, to));
}
